
// src/pages/admin/MathClasses.js

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Checkbox,
  ListItemText
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import PasswordRoundedIcon from '@mui/icons-material/PasswordRounded';
import Chip from '@mui/material/Chip';
import DashboardLayout from '../components/DashboardLayout';
import AdminActionGroup from '../components/AdminActionGroup';
import UserProfileDialog from '../components/UserProfileDialog';
import api from '../services/api';
import {
  fetchUsers,
  registerUser,
  deleteUser,
  lockUserAccount,
  unlockUserAccount,
  changeUserPassword
} from '../services/user';

const gradeOptions = ['7', '8', '9', '10', '11', '12'];
const mathSubjects = ['Mathematics', 'Mathematical Literacy'];
const curriculumOptions = ['CAPS', 'IEB', 'CAMBRIDGE', 'PEARSON EDEXCEL'];

const defaultTutorForm = {
  name: '',
  surname: '',
  email: '',
  password: '',
  phone: '',
  country: '',
  province: '',
  town: '',
  suburb: '',
  curricula: []
};
const defaultStudentForm = {
  name: '',
  surname: '',
  email: '',
  password: '',
  gradeLevel: '',
  tutorIds: [],
  phone: '',
  country: '',
  province: '',
  town: '',
  suburb: '',
  curricula: []
};
const defaultClassForm = {
  mathSubject: 'Mathematics',
  gradeLevel: '',
  startTime: '',
  endTime: '',
  scheduleLabel: '',
  subject: '',
  tutorId: '',
  studentIds: []
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
};

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const normalizeISO = (localValue) => {
  if (!localValue) return null;
  const date = new Date(localValue);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
};

const formatLocationSummary = (user) => {
  const parts = [user?.suburb, user?.town, user?.province, user?.country]
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : '--';
};

const formatCurriculaSummary = (user) => {
  const list = Array.isArray(user?.curricula) ? user.curricula.filter(Boolean) : [];
  return list.length ? list.join(', ') : '--';
};

const StatusChip = ({ locked }) => (
  <Chip
    size="small"
    label={locked ? 'Locked' : 'Active'}
    color={locked ? 'warning' : 'success'}
    variant={locked ? 'outlined' : 'filled'}
  />
);

const MathClasses = () => {
  const [activeTab, setActiveTab] = useState('classes');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);

  const [tutorDialog, setTutorDialog] = useState({ open: false, loading: false });
  const [studentDialog, setStudentDialog] = useState({ open: false, loading: false });
  const [classDialog, setClassDialog] = useState({ open: false, loading: false, mode: 'create', data: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, loading: false, title: '', message: '', action: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);
  const [viewDialog, setViewDialog] = useState({ open: false, user: null });
  const [passwordDialog, setPasswordDialog] = useState({
    open: false,
    user: null,
    loading: false,
    newPassword: '',
    confirmPassword: ''
  });

  const [tutorForm, setTutorForm] = useState(defaultTutorForm);
  const [studentForm, setStudentForm] = useState(defaultStudentForm);
  const [classForm, setClassForm] = useState(defaultClassForm);

  const teacherMap = useMemo(() => {
    const map = new Map();
    teachers.forEach((tutor) => {
      if (tutor?._id) map.set(String(tutor._id), tutor);
    });
    return map;
  }, [teachers]);

  const studentMap = useMemo(() => {
    const map = new Map();
    students.forEach((student) => {
      if (student?._id) map.set(String(student._id), student);
    });
    return map;
  }, [students]);

  const getDisplayName = useCallback(
    (user) => `${(user?.name || '')} ${(user?.surname || '')}`.trim() || user?.email || 'User',
    [],
  );

  const getSubjectNamesForUser = useCallback((user) => {
    const raw = Array.isArray(user?.subjectIds)
      ? user.subjectIds
      : Array.isArray(user?.subjects)
        ? user.subjects
        : [];
    if (!raw.length) return '-';
    const names = raw
      .map((entry) => {
        if (!entry) return null;
        if (typeof entry === 'object') return entry.name || entry.code || entry._id || null;
        return String(entry);
      })
      .filter(Boolean);
    return names.length ? names.join(', ') : '-';
  }, []);

  const loadTeachers = useCallback(async () => {
    const list = await fetchUsers({ role: 'tutor', program: 'math' });
    setTeachers(Array.isArray(list) ? list : []);
  }, []);

  const loadStudents = useCallback(async () => {
    const list = await fetchUsers({ role: 'student', program: 'math' });
    setStudents(Array.isArray(list) ? list : []);
  }, []);

  const loadClasses = useCallback(async () => {
    const response = await api.get('/session', { params: { program: 'math' } });
    const list = Array.isArray(response.data) ? response.data : [];
    setClasses(list);
  }, []);

  const refreshAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await Promise.all([loadTeachers(), loadStudents(), loadClasses()]);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, [loadClasses, loadStudents, loadTeachers]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const filteredTeachers = useMemo(() => {
    if (!searchTerm.trim()) return teachers;
    const lowered = searchTerm.trim().toLowerCase();
    return teachers.filter((t) => {
      const name = `${(t.name || '')} ${(t.surname || '')}`.trim().toLowerCase();
      const email = (t.email || '').toLowerCase();
      return name.includes(lowered) || email.includes(lowered);
    });
  }, [teachers, searchTerm]);

  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return students;
    const lowered = searchTerm.trim().toLowerCase();
    return students.filter((s) => {
      const name = `${(s.name || '')} ${(s.surname || '')}`.trim().toLowerCase();
      const email = (s.email || '').toLowerCase();
      return name.includes(lowered) || email.includes(lowered);
    });
  }, [students, searchTerm]);

  const filteredClasses = useMemo(() => {
    if (!searchTerm.trim()) return classes;
    const lowered = searchTerm.trim().toLowerCase();
    return classes.filter((cls) => {
      const subject = (cls.subject || cls.mathSubject || '').toLowerCase();
      const grade = String(cls.gradeLevel || '');
      const tutor = teacherMap.get(String(cls.tutorId));
      const tutorName = tutor ? `${(tutor.name || '')} ${(tutor.surname || '')}`.trim().toLowerCase() : '';
      return subject.includes(lowered) || grade.includes(lowered) || tutorName.includes(lowered);
    });
  }, [classes, searchTerm, teacherMap]);

  const resetTutorForm = () => setTutorForm(defaultTutorForm);
  const resetStudentForm = () => setStudentForm(defaultStudentForm);
  const resetClassForm = () => setClassForm(defaultClassForm);
  const openTutorDialog = () => {
    resetTutorForm();
    setTutorDialog({ open: true, loading: false });
  };

  const openStudentDialog = () => {
    resetStudentForm();
    setStudentDialog({ open: true, loading: false });
  };

  const openClassDialog = (mode, cls = null) => {
    if (mode === 'edit' && cls) {
      setClassForm({
        mathSubject: cls.mathSubject || 'Mathematics',
        gradeLevel: cls.gradeLevel != null ? String(cls.gradeLevel) : '',
        startTime: toDateTimeLocalValue(cls.startTime),
        endTime: toDateTimeLocalValue(cls.endTime),
        scheduleLabel: cls.scheduleLabel || '',
        subject: cls.subject || '',
        tutorId: cls.tutorId ? String(cls.tutorId) : '',
        studentIds: Array.isArray(cls.studentIds) ? cls.studentIds.map(String) : []
      });
      setClassDialog({ open: true, loading: false, mode, data: cls });
    } else {
      resetClassForm();
      setClassDialog({ open: true, loading: false, mode: 'create', data: null });
    }
  };

  const closeTutorDialog = () => setTutorDialog({ open: false, loading: false });
  const closeStudentDialog = () => setStudentDialog({ open: false, loading: false });
  const closeClassDialog = () => setClassDialog({ open: false, loading: false, mode: 'create', data: null });

  const handleTutorSubmit = async (event) => {
    event.preventDefault();
    if (!tutorForm.name || !tutorForm.surname || !tutorForm.email || !tutorForm.password) {
      setFeedback({ type: 'error', message: 'All tutor fields are required.' });
      return;
    }
    setTutorDialog((prev) => ({ ...prev, loading: true }));
    try {
      await registerUser({ ...tutorForm, role: 'tutor', program: 'math' });
      setFeedback({ type: 'success', message: 'Tutor added successfully.' });
      closeTutorDialog();
      await loadTeachers();
    } catch (err) {
      setTutorDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to create tutor.' });
    }
  };

  const handleStudentSubmit = async (event) => {
    event.preventDefault();
    if (!studentForm.name || !studentForm.surname || !studentForm.email || !studentForm.password || !studentForm.gradeLevel) {
      setFeedback({ type: 'error', message: 'All learner fields are required except tutors.' });
      return;
    }
    const gradeValue = Number(studentForm.gradeLevel);
    if (!Number.isInteger(gradeValue) || gradeValue < 7 || gradeValue > 12) {
      setFeedback({ type: 'error', message: 'Grade must be between 7 and 12.' });
      return;
    }
    setStudentDialog((prev) => ({ ...prev, loading: true }));
    try {
      await registerUser({
        ...studentForm,
        grade: gradeValue,
        role: 'student',
        program: 'math',
        tutorIds: studentForm.tutorIds
      });
      setFeedback({ type: 'success', message: 'Learner added successfully.' });
      closeStudentDialog();
      await loadStudents();
    } catch (err) {
      setStudentDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to create learner.' });
    }
  };

  const handleClassSave = async (event) => {
    event.preventDefault();
    if (!classForm.gradeLevel) {
      setFeedback({ type: 'error', message: 'Grade level is required.' });
      return;
    }
    const gradeValue = Number(classForm.gradeLevel);
    if (!Number.isInteger(gradeValue) || gradeValue < 7 || gradeValue > 12) {
      setFeedback({ type: 'error', message: 'Grade must be between 7 and 12.' });
      return;
    }
    if (!classForm.startTime || !classForm.endTime) {
      setFeedback({ type: 'error', message: 'Start and end time are required.' });
      return;
    }
    if (!classForm.tutorId) {
      setFeedback({ type: 'error', message: 'Select a tutor for the class.' });
      return;
    }

    const payload = {
      program: 'math',
      mathSubject: classForm.mathSubject,
      gradeLevel: gradeValue,
      startTime: normalizeISO(classForm.startTime),
      endTime: normalizeISO(classForm.endTime),
      tutorId: classForm.tutorId,
      studentIds: classForm.studentIds,
      scheduleLabel: classForm.scheduleLabel || undefined,
      subject: classForm.subject || undefined
    };

    if (!payload.startTime || !payload.endTime) {
      setFeedback({ type: 'error', message: 'Invalid start or end time.' });
      return;
    }

    setClassDialog((prev) => ({ ...prev, loading: true }));

    try {
      if (classDialog.mode === 'create') {
        await api.post('/session/create', payload);
        setFeedback({ type: 'success', message: 'Class scheduled successfully.' });
      } else if (classDialog.data?._id) {
        const classId = classDialog.data._id;
        const originalStart = toDateTimeLocalValue(classDialog.data.startTime);
        const originalEnd = toDateTimeLocalValue(classDialog.data.endTime);
        const reschedule = classForm.startTime !== originalStart || classForm.endTime !== originalEnd;
        if (reschedule) {
          await api.patch(`/session/${classId}`, {
            startTime: payload.startTime,
            endTime: payload.endTime
          });
        }
        await api.patch(`/session/${classId}/roster`, {
          tutorId: payload.tutorId,
          studentIds: payload.studentIds,
          subject: payload.subject,
          gradeLevel: payload.gradeLevel,
          mathSubject: payload.mathSubject,
          scheduleLabel: payload.scheduleLabel,
          program: 'math'
        });
        setFeedback({ type: 'success', message: 'Class updated successfully.' });
      }
      closeClassDialog();
      await loadClasses();
    } catch (err) {
      setClassDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.response?.data?.message || err?.message || 'Failed to save class.' });
    }
  };

  const requestDeleteUser = (userId, label) => {
    if (!userId) return;
    setConfirmDialog({
      open: true,
      loading: false,
      title: 'Remove user',
      message: `Delete ${label}? This action cannot be undone.`,
      action: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await deleteUser(userId);
          setFeedback({ type: 'success', message: 'User removed successfully.' });
          await Promise.all([loadTeachers(), loadStudents()]);
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
        } catch (err) {
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
          setFeedback({ type: 'error', message: err?.message || 'Failed to delete user.' });
        }
      }
    });
  };

  const requestDeleteClass = (cls) => {
    if (!cls?._id) return;
    setConfirmDialog({
      open: true,
      loading: false,
      title: 'Delete class',
      message: `Delete ${cls.subject || cls.mathSubject || 'this class'}?`,
      action: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          await api.delete(`/session/${cls._id}`);
          setFeedback({ type: 'success', message: 'Class deleted successfully.' });
          await loadClasses();
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
        } catch (err) {
          setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
          setFeedback({ type: 'error', message: err?.response?.data?.message || err?.message || 'Failed to delete class.' });
        }
      }
    });
  };

  const upsertStudentLocally = useCallback((updatedPayload) => {
    const updated = updatedPayload?.user || updatedPayload;
    if (!updated?._id) return;
    setStudents((prev) =>
      prev.map((item) => (String(item._id) === String(updated._id) ? { ...item, ...updated } : item))
    );
  }, []);

  const upsertTutorLocally = useCallback((updatedPayload) => {
    const updated = updatedPayload?.user || updatedPayload;
    if (!updated?._id) return;
    setTeachers((prev) =>
      prev.map((item) => (String(item._id) === String(updated._id) ? { ...item, ...updated } : item))
    );
  }, []);

  const handleLockUser = useCallback(
    (user, action, type) => {
      if (!user?._id) return;
      const lockAction = action === 'lock';
      const isTutorType = type === 'tutor';
      const roleLabel = isTutorType ? 'tutor' : 'learner';
      const displayName = `${(user.name || '')} ${(user.surname || '')}`.trim() || user.email || `this ${roleLabel}`;
      setConfirmDialog({
        open: true,
        loading: false,
        title: lockAction ? `Lock ${roleLabel} account` : `Unlock ${roleLabel} account`,
        message: lockAction
          ? `Lock ${displayName}? They will no longer be able to sign in.`
          : `Unlock ${displayName} so they can sign in again?`,
        action: async () => {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          try {
            const updated = lockAction ? await lockUserAccount(user._id) : await unlockUserAccount(user._id);
            if (isTutorType) upsertTutorLocally(updated);
            else upsertStudentLocally(updated);
            setFeedback({
              type: 'success',
              message: lockAction ? `${isTutorType ? 'Tutor' : 'Learner'} locked successfully.` : `${isTutorType ? 'Tutor' : 'Learner'} unlocked successfully.`
            });
            setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
          } catch (err) {
            setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null });
            setFeedback({ type: 'error', message: err?.message || 'Failed to update learner lock state.' });
          }
        }
      });
    },
    [upsertStudentLocally, upsertTutorLocally]
  );

  const openResetPasswordDialog = useCallback((user) => {
    if (!user?._id) return;
    setPasswordDialog({ open: true, user, loading: false, newPassword: '', confirmPassword: '' });
  }, []);

  const handleResetPassword = useCallback(async () => {
    if (!passwordDialog.user?._id) return;
    if (!passwordDialog.newPassword || passwordDialog.newPassword.length < 8) {
      setFeedback({ type: 'error', message: 'New password must be at least 8 characters.' });
      return;
    }
    if (passwordDialog.newPassword !== passwordDialog.confirmPassword) {
      setFeedback({ type: 'error', message: 'Passwords do not match.' });
      return;
    }
    setPasswordDialog((prev) => ({ ...prev, loading: true }));
    try {
      await changeUserPassword({ userId: passwordDialog.user._id, newPassword: passwordDialog.newPassword });
      setFeedback({ type: 'success', message: 'Password reset successfully.' });
      setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' });
    } catch (err) {
      setPasswordDialog((prev) => ({ ...prev, loading: false }));
      setFeedback({ type: 'error', message: err?.message || 'Failed to reset password.' });
    }
  }, [passwordDialog]);
  const handleMenuOpen = (event, type, data) => {
    setMenuAnchor(event.currentTarget);
    setMenuRow({ type, data });
  };

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const classColumns = useMemo(
    () => [
      {
        field: 'subject',
        headerName: 'Class',
        flex: 1.2,
        minWidth: 200,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {row.subject || row.mathSubject || 'Class'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Grade {row.gradeLevel ?? '-'}
            </Typography>
          </Stack>
        )
      },
      {
        field: 'tutor',
        headerName: 'Tutor',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }) => {
          const tutor = teacherMap.get(String(row.tutorId));
          return tutor ? `${(tutor.name || '')} ${(tutor.surname || '')}`.trim() : '-';
        }
      },
      {
        field: 'schedule',
        headerName: 'Schedule',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body2">{formatDateTime(row.startTime)}</Typography>
            <Typography variant="body2" color="text.secondary">{formatDateTime(row.endTime)}</Typography>
          </Stack>
        )
      },
      {
        field: 'students',
        headerName: 'Learners',
        width: 140,
        renderCell: ({ row }) => {
          const learners = Array.isArray(row.studentIds)
            ? row.studentIds
                .map((id) => studentMap.get(String(id)))
                .filter(Boolean)
                .map((student) => `${(student.name || '')} ${(student.surname || '')}`.trim())
            : [];
          const label = learners.length ? `${learners.length} enrolled` : 'None';
          return learners.length ? (
            <Tooltip title={learners.join(', ')}>
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          );
        }
      },
      {
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <IconButton size="small" onClick={(event) => handleMenuOpen(event, 'class', row)}>
            <MoreVertRoundedIcon fontSize="small" />
          </IconButton>
        )
      }
    ],
    [studentMap, teacherMap]
  );

  const tutorColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Tutor',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {`${(row.name || '')} ${(row.surname || '')}`.trim() || 'Unnamed'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.email || '-'}
            </Typography>
          </Stack>
        )
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <StatusChip locked={Boolean(row.isLocked)} />
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 150,
        renderCell: ({ row }) => row?.phone || '--'
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => formatLocationSummary(row)
      },
      {
        field: 'curricula',
        headerName: 'Curricula',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => formatCurriculaSummary(row)
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 1,
        minWidth: 180,
        valueGetter: ({ value }) => formatDateTime(value)
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <IconButton size="small" aria-label="View profile" onClick={() => setViewDialog({ open: true, user: row })}>
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="More actions" onClick={(event) => handleMenuOpen(event, 'tutor', row)}>
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        )
      }
    ],
    []
  );

  const getTutorNamesForStudent = useCallback(
    (student) => {
      if (!Array.isArray(student?.tutorIds) || !student.tutorIds.length) return '-';
      return student.tutorIds
        .map((id) => {
          const tutor = typeof id === 'object' && id !== null ? id : teacherMap.get(String(id));
          if (!tutor) return null;
          return `${(tutor.name || '')} ${(tutor.surname || '')}`.trim();
        })
        .filter(Boolean)
        .join(', ');
    },
    [teacherMap]
  );

  const studentColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Learner',
        flex: 1.4,
        minWidth: 220,
        renderCell: ({ row }) => (
          <Stack spacing={0.25}>
            <Typography variant="body1" fontWeight={600}>
              {`${(row.name || '')} ${(row.surname || '')}`.trim() || 'Unnamed'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {row.email || '-'}
            </Typography>
          </Stack>
        )
      },
      {
        field: 'grade',
        headerName: 'Grade',
        width: 100,
        renderCell: ({ row }) => row.grade ?? '-'
      },
      {
        field: 'tutors',
        headerName: 'Tutors',
        flex: 1.2,
        minWidth: 200,
        renderCell: ({ row }) => getTutorNamesForStudent(row)
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        sortable: false,
        renderCell: ({ row }) => <StatusChip locked={Boolean(row.isLocked)} />
      },
      {
        field: 'phone',
        headerName: 'Phone',
        width: 150,
        renderCell: ({ row }) => row?.phone || '--'
      },
      {
        field: 'location',
        headerName: 'Location',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }) => formatLocationSummary(row)
      },
      {
        field: 'curricula',
        headerName: 'Curricula',
        flex: 1,
        minWidth: 200,
        renderCell: ({ row }) => formatCurriculaSummary(row)
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 1,
        minWidth: 180,
        valueGetter: ({ value }) => formatDateTime(value)
      },
      {
        field: 'actions',
        headerName: '',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <IconButton size="small" aria-label="View profile" onClick={() => setViewDialog({ open: true, user: row })}>
              <VisibilityRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="More actions" onClick={(event) => handleMenuOpen(event, 'student', row)}>
              <MoreVertRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        )
      }
    ],
    [getTutorNamesForStudent]
  );

  const rows = activeTab === 'classes' ? filteredClasses : activeTab === 'teachers' ? filteredTeachers : filteredStudents;
  const columns = activeTab === 'classes' ? classColumns : activeTab === 'teachers' ? tutorColumns : studentColumns;

  const handleMenuAction = (action) => {
    const current = menuRow;
    closeMenu();
    if (!current) return;
    const { type, data } = current;
    if (type === 'class') {
      if (action === 'edit') {
        openClassDialog('edit', data);
      } else if (action === 'delete') {
        requestDeleteClass(data);
      }
    } else if (type === 'tutor') {
      if (action === 'view') {
        setViewDialog({ open: true, user: data });
      } else if (action === 'reset-password') {
        openResetPasswordDialog(data);
      } else if (action === 'lock') {
        handleLockUser(data, 'lock', 'tutor');
      } else if (action === 'unlock') {
        handleLockUser(data, 'unlock', 'tutor');
      } else if (action === 'delete') {
        requestDeleteUser(data._id, `${(data.name || '')} ${(data.surname || '')}`.trim() || data.email || 'this tutor');
      }
    } else if (type === 'student') {
      if (action === 'view') {
        setViewDialog({ open: true, user: data });
      } else if (action === 'reset-password') {
        openResetPasswordDialog(data);
      } else if (action === 'lock') {
        handleLockUser(data, 'lock', 'student');
      } else if (action === 'unlock') {
        handleLockUser(data, 'unlock', 'student');
      } else if (action === 'delete') {
        requestDeleteUser(data._id, `${(data.name || '')} ${(data.surname || '')}`.trim() || data.email || 'this learner');
      }
    }
  };
  return (
    <DashboardLayout>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={2}>
          <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={refreshAll}>
            Refresh
          </Button>
        </Stack>

        {loading && <LinearProgress />}

        {error && (
          <Alert severity="error" onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {feedback && (
          <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
            {feedback.message}
          </Alert>
        )}

        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search by subject, tutor, or learner"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
              sx={{ width: { xs: '100%', md: 320 } }}
            />

            <AdminActionGroup align="end">
              {activeTab === 'classes' && (
                <Button variant="contained" startIcon={<EventAvailableRoundedIcon />} onClick={() => openClassDialog('create')}>
                  Schedule Class
                </Button>
              )}
              {activeTab === 'teachers' && (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openTutorDialog}>
                  Add Tutor
                </Button>
              )}
              {activeTab === 'students' && (
                <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openStudentDialog}>
                  Add Learner
                </Button>
              )}
            </AdminActionGroup>
          </Stack>
        </Paper>

        <Paper sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab value="classes" label={`Classes (${filteredClasses.length})`} />
            <Tab value="teachers" label={`Tutors (${filteredTeachers.length})`} />
            <Tab value="students" label={`Learners (${filteredStudents.length})`} />
          </Tabs>
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              getRowId={(row) => row._id || row.id}
              disableRowSelectionOnClick
              loading={loading}
              pageSizeOptions={[10, 25, 50]}
              initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } } }}
              density="comfortable"
              sx={{ border: 0, '& .MuiDataGrid-cell': { alignItems: 'center' } }}
              onRowDoubleClick={({ row }) => {
                if (activeTab === 'classes') return;
                setViewDialog({ open: true, user: row });
              }}
            />
          </Box>
        </Paper>
      </Stack>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
        {menuRow?.type === 'class' && (
          <>
            <MenuItem onClick={() => handleMenuAction('edit')}>
              <EditRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Edit class
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction('delete')}>
              <DeleteRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Delete class
            </MenuItem>
          </>
        )}
        {(menuRow?.type === 'tutor' || menuRow?.type === 'student') && (
          <>
            <MenuItem onClick={() => handleMenuAction('view')}>
              <VisibilityRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              View profile
            </MenuItem>
            <MenuItem onClick={() => handleMenuAction('reset-password')}>
              <PasswordRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Reset password
            </MenuItem>
            {(menuRow?.type === 'student' || menuRow?.type === 'tutor') &&
              (menuRow?.data?.isLocked ? (
                <MenuItem onClick={() => handleMenuAction('unlock')}>
                  <LockOpenRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                  Unlock account
                </MenuItem>
              ) : (
                <MenuItem onClick={() => handleMenuAction('lock')}>
                  <LockRoundedIcon fontSize="small" sx={{ mr: 1 }} />
                  Lock account
                </MenuItem>
              ))}
            <MenuItem onClick={() => handleMenuAction('delete')}>
              <DeleteRoundedIcon fontSize="small" sx={{ mr: 1 }} />
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
      <UserProfileDialog
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, user: null })}
        user={viewDialog.user}
        title="Math Programme User Profile"
        getDisplayName={getDisplayName}
        getTutorNames={getTutorNamesForStudent}
        getSubjectNames={getSubjectNamesForUser}
      />
      <Dialog open={tutorDialog.open} onClose={tutorDialog.loading ? undefined : closeTutorDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Mathematics Tutor</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="tutor-form" onSubmit={handleTutorSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="First name"
                  value={tutorForm.name}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, name: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Surname"
                  value={tutorForm.surname}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, surname: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Email"
                  type="email"
                  value={tutorForm.email}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, email: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Temporary password"
                  value={tutorForm.password}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, password: event.target.value }))}
                  helperText="Minimum 8 characters recommended."
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={tutorForm.phone}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, phone: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Country"
                  value={tutorForm.country}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, country: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Province"
                  value={tutorForm.province}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, province: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Town / City"
                  value={tutorForm.town}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, town: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Suburb"
                  value={tutorForm.suburb}
                  onChange={(event) => setTutorForm((prev) => ({ ...prev, suburb: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="math-tutor-curricula-label">Curricula</InputLabel>
                  <Select
                    labelId="math-tutor-curricula-label"
                    multiple
                    label="Curricula"
                    value={tutorForm.curricula}
                    onChange={(event) => setTutorForm((prev) => ({ ...prev, curricula: event.target.value }))}
                    input={<OutlinedInput label="Curricula" />}
                    renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                  >
                    {curriculumOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={tutorForm.curricula.includes(option)} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeTutorDialog} disabled={tutorDialog.loading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="tutor-form" disabled={tutorDialog.loading}>
            {tutorDialog.loading ? 'Saving...' : 'Add tutor'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={studentDialog.open} onClose={studentDialog.loading ? undefined : closeStudentDialog} maxWidth="md" fullWidth>
        <DialogTitle>Add Mathematics Learner</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="student-form" onSubmit={handleStudentSubmit} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="First name"
                  value={studentForm.name}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, name: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Surname"
                  value={studentForm.surname}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, surname: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Email"
                  type="email"
                  value={studentForm.email}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, email: event.target.value }))}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Temporary password"
                  value={studentForm.password}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, password: event.target.value }))}
                  helperText="Minimum 8 characters recommended."
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel id="math-grade-label">Grade</InputLabel>
                  <Select
                    labelId="math-grade-label"
                    label="Grade"
                    value={studentForm.gradeLevel}
                    onChange={(event) => setStudentForm((prev) => ({ ...prev, gradeLevel: event.target.value }))}
                  >
                    {gradeOptions.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="math-student-tutors-label">Tutors</InputLabel>
                  <Select
                    labelId="math-student-tutors-label"
                    multiple
                    value={studentForm.tutorIds}
                    onChange={(event) => setStudentForm((prev) => ({ ...prev, tutorIds: event.target.value }))}
                    input={<OutlinedInput label="Tutors" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((id) => {
                          const tutor = teacherMap.get(String(id));
                          if (!tutor) return null;
                          return `${(tutor.name || '')} ${(tutor.surname || '')}`.trim();
                        })
                        .filter(Boolean)
                        .join(', ');
                    }}
                  >
                    {teachers.map((tutor) => (
                      <MenuItem key={tutor._id} value={tutor._id}>
                        <Checkbox checked={studentForm.tutorIds.includes(tutor._id)} />
                        <ListItemText primary={`${(tutor.name || '')} ${(tutor.surname || '')}`.trim()} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Phone"
                  value={studentForm.phone}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, phone: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Country"
                  value={studentForm.country}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, country: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Province"
                  value={studentForm.province}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, province: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Town / City"
                  value={studentForm.town}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, town: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Suburb"
                  value={studentForm.suburb}
                  onChange={(event) => setStudentForm((prev) => ({ ...prev, suburb: event.target.value }))}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="math-student-curricula-label">Curricula</InputLabel>
                  <Select
                    labelId="math-student-curricula-label"
                    multiple
                    label="Curricula"
                    value={studentForm.curricula}
                    onChange={(event) => setStudentForm((prev) => ({ ...prev, curricula: event.target.value }))}
                    input={<OutlinedInput label="Curricula" />}
                    renderValue={(selected) => (selected.length ? selected.join(', ') : 'None')}
                  >
                    {curriculumOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        <Checkbox checked={studentForm.curricula.includes(option)} />
                        <ListItemText primary={option} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStudentDialog} disabled={studentDialog.loading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="student-form" disabled={studentDialog.loading}>
            {studentDialog.loading ? 'Saving...' : 'Add learner'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={classDialog.open} onClose={classDialog.loading ? undefined : closeClassDialog} maxWidth="md" fullWidth>
        <DialogTitle>{classDialog.mode === 'edit' ? 'Edit Class' : 'Schedule Class'}</DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="class-form" onSubmit={handleClassSave} sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel id="math-subject-label">Subject</InputLabel>
                  <Select
                    labelId="math-subject-label"
                    label="Subject"
                    value={classForm.mathSubject}
                    onChange={(event) => setClassForm((prev) => ({ ...prev, mathSubject: event.target.value }))}
                  >
                    {mathSubjects.map((subject) => (
                      <MenuItem key={subject} value={subject}>
                        {subject}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!classForm.gradeLevel}>
                  <InputLabel id="class-grade-label">Grade</InputLabel>
                  <Select
                    labelId="class-grade-label"
                    label="Grade"
                    value={classForm.gradeLevel}
                    onChange={(event) => setClassForm((prev) => ({ ...prev, gradeLevel: event.target.value }))}
                  >
                    {gradeOptions.map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        {grade}
                      </MenuItem>
                    ))}
                  </Select>
                  {!classForm.gradeLevel && <FormHelperText>Select a grade level</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Start time"
                  type="datetime-local"
                  value={classForm.startTime}
                  onChange={(event) => setClassForm((prev) => ({ ...prev, startTime: event.target.value }))}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="End time"
                  type="datetime-local"
                  value={classForm.endTime}
                  onChange={(event) => setClassForm((prev) => ({ ...prev, endTime: event.target.value }))}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!classForm.tutorId}>
                  <InputLabel id="class-tutor-label">Tutor</InputLabel>
                  <Select
                    labelId="class-tutor-label"
                    label="Tutor"
                    value={classForm.tutorId}
                    onChange={(event) => setClassForm((prev) => ({ ...prev, tutorId: event.target.value }))}
                  >
                    <MenuItem value="">
                      <em>Select tutor</em>
                    </MenuItem>
                    {teachers.map((tutor) => (
                      <MenuItem key={tutor._id} value={tutor._id}>
                        {`${(tutor.name || '')} ${(tutor.surname || '')}`.trim() || tutor.email || 'Tutor'}
                      </MenuItem>
                    ))}
                  </Select>
                  {!classForm.tutorId && <FormHelperText>Select a tutor</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Schedule label"
                  value={classForm.scheduleLabel}
                  onChange={(event) => setClassForm((prev) => ({ ...prev, scheduleLabel: event.target.value }))}
                  fullWidth
                  placeholder="Optional label shown in schedule"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Display subject"
                  value={classForm.subject}
                  onChange={(event) => setClassForm((prev) => ({ ...prev, subject: event.target.value }))}
                  fullWidth
                  placeholder="Overrides default class title (optional)"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="class-students-label">Learners</InputLabel>
                  <Select
                    labelId="class-students-label"
                    multiple
                    value={classForm.studentIds}
                    onChange={(event) => setClassForm((prev) => ({ ...prev, studentIds: event.target.value }))}
                    input={<OutlinedInput label="Learners" />}
                    renderValue={(selected) => {
                      if (!selected.length) return 'None';
                      return selected
                        .map((id) => {
                          const student = studentMap.get(String(id));
                          if (!student) return null;
                          return `${(student.name || '')} ${(student.surname || '')}`.trim();
                        })
                        .filter(Boolean)
                        .join(', ');
                    }}
                  >
                    {students.map((student) => (
                      <MenuItem key={student._id} value={student._id}>
                        <Checkbox checked={classForm.studentIds.includes(student._id)} />
                        <ListItemText
                          primary={`${(student.name || '')} ${(student.surname || '')}`.trim()}
                          secondary={`Grade ${student.grade ?? '-'}`}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeClassDialog} disabled={classDialog.loading}>
            Cancel
          </Button>
          <Button variant="contained" type="submit" form="class-form" disabled={classDialog.loading}>
            {classDialog.loading ? 'Saving...' : classDialog.mode === 'edit' ? 'Save changes' : 'Create class'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDialog.open} onClose={confirmDialog.loading ? undefined : () => setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog({ open: false, loading: false, title: '', message: '', action: null })}
            disabled={confirmDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (confirmDialog.action) {
                await confirmDialog.action();
              }
            }}
            disabled={confirmDialog.loading}
          >
            {confirmDialog.loading ? 'Working...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={passwordDialog.open}
        onClose={
          passwordDialog.loading
            ? undefined
            : () => setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              User: {passwordDialog.user ? `${(passwordDialog.user.name || '')} ${(passwordDialog.user.surname || '')}`.trim() || passwordDialog.user.email : ''}
            </Typography>
            <TextField
              label="New password"
              type="password"
              value={passwordDialog.newPassword}
              onChange={(event) => setPasswordDialog((prev) => ({ ...prev, newPassword: event.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Confirm password"
              type="password"
              value={passwordDialog.confirmPassword}
              onChange={(event) => setPasswordDialog((prev) => ({ ...prev, confirmPassword: event.target.value }))}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPasswordDialog({ open: false, user: null, loading: false, newPassword: '', confirmPassword: '' })}
            disabled={passwordDialog.loading}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleResetPassword} disabled={passwordDialog.loading}>
            {passwordDialog.loading ? 'Resetting...' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default MathClasses;
